import {
  createJournalEntry,
  getExistingAuthorizationToken,
  recordInteractionEvent,
  retrieveGameState,
  sendComprehensionData,
  sendStruggleData,
  storeGameState,
  storeUserGeneratedImage,
} from './BackendInterface';
import { dataResidencyManager } from '@peekapak/data-residency';

// The container performs the login, so it is the only party that knows which region issued
// this player's token. Unity's own DataResidencyManager learns the region only from its
// in-game login flows, which never run on WebGL, so without this handshake it stays on its
// USA default and sends ARE players' requests to the US backend (MYP-862).
//
// The value is the canonical SupportedDataResidency string; Unity's enum uses the same
// names, so it parses the reply directly.
//
// Unity asks once per session and latches the answer, so a single reply is not enough —
// the first one can be sent too early to be right. We therefore remember who asked and
// re-send on two triggers: when gameInstance becomes available (Unity can ask before
// GameContainer has assigned it), and whenever residency changes (on standalone the login
// happens in this app, so an early ask sees the USA default). Unity re-applying the same
// value is harmless.
let dataResidencyRequester = null;
let isSubscribedToDataResidencyChanges = false;

// Also the flush GameContainer calls once gameInstance exists. Silently does nothing until
// both an asker and an instance are present, which is what makes it safe to call on spec.
const sendDataResidency = () => {
  if (!dataResidencyRequester || !peekavilleWebContainer.gameInstance) {
    return;
  }

  const { unityGameObject, unityGameObjectMethod } = dataResidencyRequester;
  peekavilleWebContainer.gameInstance.SendMessage(
    unityGameObject,
    unityGameObjectMethod,
    dataResidencyManager.getCurrentDataResidency(),
  );
};

const getDataResidencyForUnity3D = (unityGameObject, unityGameObjectMethod) => {
  dataResidencyRequester = { unityGameObject, unityGameObjectMethod };

  if (!isSubscribedToDataResidencyChanges) {
    isSubscribedToDataResidencyChanges = true;
    dataResidencyManager.subscribeToChanges(sendDataResidency);
  }

  sendDataResidency();
};

const getAuthToken3D = (unityGameObject, unityGameObjectMethod) => {
  const token = getExistingAuthorizationToken();

  if (token && peekavilleWebContainer.gameInstance) {
    peekavilleWebContainer.gameInstance.SendMessage(
      unityGameObject,
      unityGameObjectMethod,
      token,
    );

    return;
  }

  peekavilleWebContainer.gameInstance.SendMessage(
    unityGameObject,
    unityGameObjectMethod,
    '',
  );
};

const retrieveGameStateForUnity3D = (
  unityGameObject,
  unityGameObjectMethod,
) => {
  retrieveGameState(handleGoodResult, () => {
    console.error(`DEBUG: Retrieve game state for Unity WebGL failed`);
    handleBadResult(unityGameObject, unityGameObjectMethod);
  });

  function handleGoodResult(response) {
    if (peekavilleWebContainer.gameInstance) {
      peekavilleWebContainer.gameInstance.SendMessage(
        unityGameObject,
        unityGameObjectMethod,
        JSON.stringify(response),
      );
    }
  }
};

const storeGameStateForUnity3D = (
  newGameState,
  unityGameObject,
  unityGameObjectMethod,
) => {
  storeGameState(JSON.parse(newGameState), handleGoodResult, () => {
    console.error(`DEBUG: Store game state for Unity WebGL failed`);
    handleBadResult(unityGameObject, unityGameObjectMethod);
  });

  function handleGoodResult() {
    if (peekavilleWebContainer.gameInstance) {
      peekavilleWebContainer.gameInstance.SendMessage(
        unityGameObject,
        unityGameObjectMethod,
        'success',
      );
    }
  }
};

const createJournalEntryForUnity3D = (
  journalEntry,
  unityGameObject,
  unityGameObjectMethod,
) => {
  createJournalEntry(JSON.parse(journalEntry), handleGoodResult, () => {
    console.error(`DEBUG: Store game state for Unity WebGL failed`);
    handleBadResult(unityGameObject, unityGameObjectMethod);
  });

  function handleGoodResult() {
    if (peekavilleWebContainer.gameInstance) {
      peekavilleWebContainer.gameInstance.SendMessage(
        unityGameObject,
        unityGameObjectMethod,
        'success',
      );
    }
  }
};

const sendStruggleDataForUnity3D = (
  struggleData,
  unityGameObject,
  unityGameObjectMethod,
) => {
  sendStruggleData(JSON.parse(struggleData), handleGoodResult, () => {
    console.error(`DEBUG: Store struggle data for Unity WebGL failed`);
    handleBadResult(unityGameObject, unityGameObjectMethod);
  });

  function handleGoodResult() {
    if (peekavilleWebContainer.gameInstance) {
      peekavilleWebContainer.gameInstance.SendMessage(
        unityGameObject,
        unityGameObjectMethod,
        'success',
      );
    }
  }
};

const sendComprehensionDataForUnity3D = (
  comprehensionData,
  unityGameObject,
  unityGameObjectMethod,
) => {
  sendComprehensionData(JSON.parse(comprehensionData), handleGoodResult, () => {
    console.error(`DEBUG: Store comprehension data for Unity WebGL failed`);
    handleBadResult(unityGameObject, unityGameObjectMethod);
  });

  function handleGoodResult() {
    if (peekavilleWebContainer.gameInstance) {
      peekavilleWebContainer.gameInstance.SendMessage(
        unityGameObject,
        unityGameObjectMethod,
        'success',
      );
    }
  }
};

const signalPlayerSignoutToUnity3D = async () => {
  if (peekavilleWebContainer.gameInstance) {
    peekavilleWebContainer.gameInstance.SendMessage(
      `Singleton<SaveManager>`,
      `OnSignout`,
    );
  }
};

const storeUserGeneratedImageForUnity3D = (
  applicationName,
  displayTitle,
  imageAsDataUri,
  unityGameObject,
  unityGameObjectMethod,
) => {
  storeUserGeneratedImage(
    applicationName,
    displayTitle,
    imageAsDataUri,
    handleGoodResult,
    () => {
      console.error(
        `DEBUG: Store letters game letter image for Unity WebGL failed`,
      );
      handleBadResult(unityGameObject, unityGameObjectMethod);
    },
  );

  function handleGoodResult() {
    if (peekavilleWebContainer.gameInstance) {
      peekavilleWebContainer.gameInstance.SendMessage(
        unityGameObject,
        unityGameObjectMethod,
        'success',
      );
    }
  }
};

const recordInteractionEventForUnity3D = (
  eventType,
  eventName,
  callbackObject,
  callbackObjectMethod,
) => {
  recordInteractionEvent(eventType, eventName);
};

function handleBadResult(unityGameObject, unityGameObjectMethod) {
  if (peekavilleWebContainer.gameInstance) {
    peekavilleWebContainer.gameInstance.SendMessage(
      unityGameObject,
      unityGameObjectMethod,
      '',
    );
  }
}

const setupUnityWebGLInterface = () => {
  /* global peekavilleWebContainer:true */
  peekavilleWebContainer.retrieveGameState = retrieveGameStateForUnity3D;
  peekavilleWebContainer.storeGameState = storeGameStateForUnity3D;
  peekavilleWebContainer.createJournalEntry = createJournalEntryForUnity3D;
  peekavilleWebContainer.sendStruggleData = sendStruggleDataForUnity3D;
  peekavilleWebContainer.sendComprehensionData =
    sendComprehensionDataForUnity3D;
  peekavilleWebContainer.signalPlayerSignout = signalPlayerSignoutToUnity3D;
  peekavilleWebContainer.storeUserGeneratedImage =
    storeUserGeneratedImageForUnity3D;
  peekavilleWebContainer.recordInteractionEvent =
    recordInteractionEventForUnity3D;
  peekavilleWebContainer.getAuthToken = getAuthToken3D;
  peekavilleWebContainer.getDataResidency = getDataResidencyForUnity3D;
  peekavilleWebContainer.flushPendingDataResidency = sendDataResidency;
};

export default setupUnityWebGLInterface;
